// data-fit LLC · DecisionFlow · GOVERN Stage
// FXRateManager.jsx · S956
//
// S956: fx_rate_set governance infrastructure wired end-to-end.
//   - RateSetApprovalPanel: new component — shows rate set metadata
//     (source, date, pair_count, content_hash, normalization_applied) +
//     single "Approve Rate Set" button → POST /rate-sets/{id}/approve.
//     One governed approval event propagates to all child exchange_rate rows.
//   - PendingRateSetsPanel: new component — lists pending rate sets for the
//     period (GET /rate-sets?fiscal_period_id=X&status=pending). Each shows
//     approve button. Shown above rate table when pending sets exist.
//   - ECBPullPanel: captures rate_set_id from pull response, passes to
//     onPullComplete(rateSetId) — parent opens RateSetApprovalPanel immediately.
//   - panel state extended: null | 'create' | 'ecb' | 'rate_set_approve'
//   - Row-level approve retained for manual rows (rate_set_id = NULL).
//   - Bulk approve serial loop retained for manual rows without a rate set.
//     Rate-set governed rows use approve_fx_rate_set() path instead.
//
// S955: C3 — base currency pill. Audit bug 6 — ECB result orientation.
// S950: ECB bulk pull — self-contained period selector.
// S247: amendment governance on Approve (row-level, retained for manual rows).
//
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: comp_admin full access; finance read-only; is_locked immutable;
//   rate_approved_by + rate_approved_at both required;
//   envelope: { exchange_rates: [...], count }

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import AmendmentBeginModal from '../admin/AmendmentBeginModal.jsx'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

const API_BASE = '/api/v1'
const PERIOD_TYPES = ['monthly', 'weekly', 'daily']
const RATE_SOURCES = ['manual', 'ecb', 'client_internal']
const GOVERNED_ENTITY_TYPE = 'exchange_rate'

const DATE_STRATEGIES = [
  { value: 'prior_close',   label: 'Last business day of prior month',  description: 'Treasury standard — rate in effect at period open' },
  { value: 'period_start',  label: 'First business day of period',       description: 'Rate on or after period start date' },
  { value: 'period_end',    label: 'Last business day of period',        description: 'Closing rate — retrospective close / true-up' },
  { value: 'specific',      label: 'Specific date',                      description: 'Pick any date within the period' },
]

const INITIAL_FORM    = { from_currency_code: '', to_currency_code: '', effective_date: '', rate: '', period_type: 'monthly', rate_source: 'manual', fiscal_period_id: '' }
const INITIAL_FILTERS = { from_currency: '', to_currency: '', is_locked: '' }

function fmt(val)         { if (val === null || val === undefined) return '—'; return String(val) }
function fmtRate(val)     { if (val === null || val === undefined) return '—'; return Number(val).toFixed(6) }
function fmtDate(val)     { if (!val) return '—'; return String(val).slice(0, 10) }
function fmtDateTime(val) { if (!val) return '—'; try { return new Date(val).toLocaleString() } catch { return String(val) } }
function getRows(payload) { if (Array.isArray(payload?.exchange_rates)) return payload.exchange_rates; if (Array.isArray(payload?.data)) return payload.data; if (Array.isArray(payload)) return payload; return [] }
function getErrorMessage(payload) {
  const msg = payload?.error?.message || payload?.message
  if (msg) return msg
  const detail = payload?.detail
  if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join('; ')
  if (typeof detail === 'string') return detail
  return 'Unexpected error.'
}

function StatusBadge({ rate }) {
  if (rate.is_locked)        return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">🔒 Locked</span>
  if (rate.rate_approved_by) return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">✓ Approved</span>
  return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">⏳ Pending</span>
}

function RateSetStatusBadge({ status }) {
  if (status === 'locked')   return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">🔒 Locked</span>
  if (status === 'approved') return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">✓ Approved</span>
  return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">⏳ Pending</span>
}

function SourceBadge({ source }) {
  const map = {
    manual:          'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
    ecb:             'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]',
    client_internal: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
    csv:             'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
    treasury:        'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  }
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${map[source] || 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>{source}</span>
}

function OutcomeBadge({ outcome }) {
  const map = {
    inserted: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
    updated:  'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
    skipped:  'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
    failed:   'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',
  }
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${map[outcome] || ''}`}>{outcome}</span>
}

async function commitAmendment(amendmentId) {
  if (!amendmentId) return
  try {
    const res = await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      console.warn('[Amendment] Commit failed:', body?.error?.message ?? res.status)
    }
  } catch (e) {
    console.warn('[Amendment] Commit network error:', e.message)
  }
}

// ---------------------------------------------------------------------------
// RateSetApprovalPanel — S956
// Shows rate set metadata and single governed approval button.
// Called after ECB pull (rate_set_id from response) or from pending sets list.
// CA approves the header once — propagates to all child exchange_rate rows.
// ---------------------------------------------------------------------------
function RateSetApprovalPanel({ rateSetId, baseCurrencyCode, onApproved, onCancel }) {
  const [rateSet, setRateSet]   = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [approving, setApproving] = useState(false)
  const [successMsg, setSuccessMsg] = useState(null)

  useEffect(() => {
    if (!rateSetId) return
    setLoading(true); setError(null)
    fetch(`${API_BASE}/exchange-rates/rate-sets/${rateSetId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(payload => {
        if (payload?.rate_set) setRateSet(payload.rate_set)
        else setError('Could not load rate set.')
      })
      .catch(e => setError(e.message || 'Network error.'))
      .finally(() => setLoading(false))
  }, [rateSetId])

  const handleApprove = async () => {
    setApproving(true); setError(null); setSuccessMsg(null)
    try {
      const res = await fetch(`${API_BASE}/exchange-rates/rate-sets/${rateSetId}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      const approved = payload?.rate_set?.rows_approved ?? payload?.rows_approved
      setSuccessMsg(
        `Rate set approved. ${approved != null ? `${approved} rates are now engine-eligible.` : ''}`
      )
      setRateSet(prev => prev ? { ...prev, status: 'approved', approved_at: new Date().toISOString() } : prev)
      if (onApproved) onApproved()
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setApproving(false)
    }
  }

  if (loading) return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading rate set…</p>
    </div>
  )

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/30 bg-[rgb(var(--color-primary))]/5 p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Approve Rate Set</h3>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
            Review the rate set details below. A single approval covers all rates in this governed batch.
          </p>
        </div>
        <button type="button" onClick={onCancel} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] mt-0.5">✕</button>
      </div>

      {rateSet && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3 flex flex-col gap-2">

          {/* Status + normalization flag */}
          <div className="flex items-center gap-2 flex-wrap">
            <RateSetStatusBadge status={rateSet.status} />
            <SourceBadge source={rateSet.source_type} />
            {rateSet.normalization_applied && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
                ⚠ Normalised
              </span>
            )}
          </div>

          {/* Rate set metadata grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-tok-xs sm:grid-cols-3">
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Name</span>
              <p className="font-medium text-[rgb(var(--color-text))] truncate">{fmt(rateSet.name)}</p>
            </div>
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Source date</span>
              <p className="font-medium font-mono text-[rgb(var(--color-text))]">{fmtDate(rateSet.source_date)}</p>
            </div>
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Base currency</span>
              <p className="font-medium font-mono text-[rgb(var(--color-text))]">{fmt(rateSet.reporting_currency)}</p>
            </div>
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Rate pairs</span>
              <p className="font-medium text-[rgb(var(--color-text))]">{rateSet.pair_count ?? '—'}</p>
            </div>
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Rate policy</span>
              <p className="font-medium text-[rgb(var(--color-text))]">{fmt(rateSet.rate_policy)}</p>
            </div>
            {rateSet.approved_at && (
              <div>
                <span className="text-[rgb(var(--color-text-muted))]">Approved at</span>
                <p className="font-medium text-[rgb(var(--color-text))]">{fmtDateTime(rateSet.approved_at)}</p>
              </div>
            )}
          </div>

          {/* Content hash — integrity fingerprint */}
          {rateSet.content_hash && (
            <div className="text-tok-xs">
              <span className="text-[rgb(var(--color-text-muted))]">Content hash (SHA-256) </span>
              <span className="font-mono text-[rgb(var(--color-text-muted))] break-all">{rateSet.content_hash}</span>
            </div>
          )}

          {/* Normalisation warning */}
          {rateSet.normalization_applied && (
            <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/20 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
              ⚠ This rate set contains rows that were normalised from reverse orientation (CA-One authorised).
              Your approval covers the derived canonical local→{baseCurrencyCode ?? 'base'} rates stored in the system.
              The original orientation and this normalisation event are permanently recorded.
            </div>
          )}
        </div>
      )}

      {error    && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}
      {successMsg && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))] flex items-center justify-between">
          {successMsg}
          <button type="button" onClick={onCancel} className="ml-3 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">Done</button>
        </div>
      )}

      {!successMsg && rateSet?.status === 'pending' && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleApprove}
            disabled={approving}
            className="rounded-lg bg-[rgb(var(--color-success))] px-4 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50"
          >
            {approving ? 'Approving…' : `Approve Rate Set${rateSet.pair_count ? ` (${rateSet.pair_count} rates)` : ''}`}
          </button>
          <button type="button" onClick={onCancel} disabled={approving}
            className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">
            Cancel
          </button>
        </div>
      )}
      {!successMsg && rateSet?.status !== 'pending' && (
        <button type="button" onClick={onCancel}
          className="self-start rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
          Close
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PendingRateSetsPanel — S956
// Lists pending rate sets for the period.
// Shown above the rate table when pending sets exist.
// Each set has its own Approve button → RateSetApprovalPanel.
// ---------------------------------------------------------------------------
function PendingRateSetsPanel({ fiscalPeriodId, baseCurrencyCode, onSetApproved, onApproveSingleSet }) {
  const [rateSets, setRateSets]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)

  const loadSets = useCallback(() => {
    setLoading(true); setError(null)
    const params = new URLSearchParams({ status: 'pending' })
    if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId))
    fetch(`${API_BASE}/exchange-rates/rate-sets?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(payload => setRateSets(payload?.rate_sets ?? []))
      .catch(e => setError(e.message || 'Network error.'))
      .finally(() => setLoading(false))
  }, [fiscalPeriodId])

  useEffect(() => { loadSets() }, [loadSets])

  // Expose refresh for parent
  useEffect(() => {
    if (onSetApproved) onSetApproved.current = loadSets
  }, [loadSets, onSetApproved])

  if (loading) return <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Checking for pending rate sets…</p>
  if (error)   return <p className="text-tok-xs text-[rgb(var(--color-danger))]">{error}</p>
  if (rateSets.length === 0) return null

  return (
    <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 p-4 flex flex-col gap-3">
      <div>
        <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-warning))]">
          ⏳ {rateSets.length} Pending Rate Set{rateSets.length > 1 ? 's' : ''} — Approval Required
        </p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
          Approve the rate set header to make all rates in the batch engine-eligible in one governed event.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        {rateSets.map(rs => (
          <div key={rs.rate_set_id}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <SourceBadge source={rs.source_type} />
                {rs.normalization_applied && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">⚠ Normalised</span>
                )}
                <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">{fmtDate(rs.source_date)}</span>
                {rs.pair_count != null && (
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rs.pair_count} pairs</span>
                )}
              </div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] truncate">{rs.name}</p>
            </div>
            <button
              type="button"
              onClick={() => onApproveSingleSet(rs.rate_set_id)}
              className="shrink-0 rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80"
            >
              Review &amp; Approve
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CreateForm — NOT amendment-governed (new manual entity, rate_set_id = NULL)
// ---------------------------------------------------------------------------
function CreateForm({ onCreated, onCancel }) {
  const [form, setForm]      = useState(INITIAL_FORM)
  const [submitting, setSub] = useState(false)
  const [error, setError]    = useState(null)
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async () => {
    setError(null)
    if (!form.from_currency_code.trim() || !form.to_currency_code.trim()) { setError('from_currency_code and to_currency_code are required.'); return }
    if (form.from_currency_code.trim().toUpperCase() === form.to_currency_code.trim().toUpperCase()) { setError('from_currency_code and to_currency_code must differ.'); return }
    if (!form.effective_date) { setError('effective_date is required.'); return }
    if (!form.rate || Number(form.rate) <= 0) { setError('rate must be a positive number.'); return }
    const body = {
      from_currency_code: form.from_currency_code.trim().toUpperCase(),
      to_currency_code:   form.to_currency_code.trim().toUpperCase(),
      effective_date:     form.effective_date,
      rate:               form.rate,
      period_type:        form.period_type,
      rate_source:        form.rate_source,
    }
    if (form.fiscal_period_id !== '' && !Number.isNaN(Number(form.fiscal_period_id))) body.fiscal_period_id = Number(form.fiscal_period_id)
    setSub(true)
    try {
      const res = await fetch(`${API_BASE}/exchange-rates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      onCreated(payload?.exchange_rate || payload?.data || payload)
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setSub(false)
    }
  }

  const inputClass = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'
  const labelClass = 'flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]'

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 p-4 flex flex-col gap-4">
      <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New Exchange Rate — Manual Entry</h3>
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        Manual rates are governed at row level. Use ECB pull or CSV import for batch governed rate sets.
      </p>
      {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className={labelClass}>From Currency *<input type="text" maxLength={3} placeholder="EUR" value={form.from_currency_code} onChange={(e) => set('from_currency_code', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>To Currency *<input type="text" maxLength={3} placeholder="USD" value={form.to_currency_code} onChange={(e) => set('to_currency_code', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Effective Date *<input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Rate *<input type="number" step="0.000001" min="0.000001" placeholder="1.234567" value={form.rate} onChange={(e) => set('rate', e.target.value)} className={inputClass} /></label>
        <label className={labelClass}>Period Type<select value={form.period_type} onChange={(e) => set('period_type', e.target.value)} className={inputClass}>{PERIOD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
        <label className={labelClass}>Rate Source<select value={form.rate_source} onChange={(e) => set('rate_source', e.target.value)} className={inputClass}>{RATE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
        <label className={labelClass}>Fiscal Period ID<input type="number" step="1" placeholder="optional" value={form.fiscal_period_id} onChange={(e) => set('fiscal_period_id', e.target.value)} className={inputClass} /></label>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={handleSubmit} disabled={submitting} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">{submitting ? 'Creating…' : 'Create Rate'}</button>
        <button type="button" onClick={onCancel} disabled={submitting} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Cancel</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ECBPullPanel — S956: captures rate_set_id from response, passes to parent
// S950: self-contained period selector + treasury-compliant ECB pull
// ---------------------------------------------------------------------------
function ECBPullPanel({ defaultFiscalPeriodId, baseCurrencyCode, onPullComplete, onCancel }) {
  const [periods, setPeriods]               = useState([])
  const [periodsLoading, setPeriodsLoading] = useState(true)
  const [periodsError, setPeriodsError]     = useState(null)
  const [selectedPeriodId, setSelectedPeriodId] = useState(defaultFiscalPeriodId ?? '')
  const [strategy, setStrategy]             = useState('prior_close')
  const [specificDate, setSpecificDate]     = useState('')
  const [pulling, setPulling]               = useState(false)
  const [result, setResult]                 = useState(null)
  const [error, setError]                   = useState(null)

  useEffect(() => {
    setPeriodsLoading(true)
    fetch(`${API_BASE}/fiscal-periods?limit=50`, { credentials: 'include' })
      .then(r => r.json())
      .then(payload => {
        const rows = payload?.fiscal_periods ?? payload?.data ?? payload ?? []
        const sorted = [...rows].sort((a, b) =>
          (b.period_start_date ?? '').localeCompare(a.period_start_date ?? '')
        )
        setPeriods(sorted)
        if (!defaultFiscalPeriodId && sorted.length) {
          const openPeriod = sorted.slice().reverse().find(p => p.period_status === 'open')
          if (openPeriod) setSelectedPeriodId(openPeriod.fiscal_period_id)
        }
      })
      .catch(e => setPeriodsError(e.message || 'Could not load fiscal periods.'))
      .finally(() => setPeriodsLoading(false))
  }, [])

  const selectedPeriod = periods.find(p => String(p.fiscal_period_id) === String(selectedPeriodId))
  const periodStart    = selectedPeriod?.period_start_date ?? null
  const periodEnd      = selectedPeriod?.period_end_date ?? null
  const periodLabel    = selectedPeriod?.period_label ?? selectedPeriod?.period_code ?? null

  const handlePull = async () => {
    setError(null); setResult(null)
    if (!selectedPeriodId || !periodStart || !periodEnd) { setError('Select a fiscal period before pulling.'); return }
    if (strategy === 'specific' && !specificDate) { setError('Select a specific date within the period.'); return }
    const body = {
      fiscal_period_id: Number(selectedPeriodId),
      period_start:     periodStart,
      period_end:       periodEnd,
      date_strategy:    strategy,
    }
    if (strategy === 'specific') body.specific_date = specificDate
    setPulling(true)
    try {
      const res = await fetch(`${API_BASE}/exchange-rates/pull-ecb`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(payload?.message || getErrorMessage(payload)); return }
      setResult(payload)
      if (payload.outcome === 'ok') {
        // S956: pass rate_set_id to parent so it can open RateSetApprovalPanel
        onPullComplete(payload.rate_set_id ?? null)
      }
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setPulling(false)
    }
  }

  const inputClass = 'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'
  const summary    = result?.summary
  const hasResults = result?.results?.length > 0

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/30 bg-[rgb(var(--color-primary))]/5 p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Pull Rates from ECB</h3>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
            Fetches all published ECB rates vs EUR for the selected period.
            Pulled rates are grouped into a governed rate set — approve the set in one event.
          </p>
        </div>
        <button type="button" onClick={onCancel} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] mt-0.5">✕</button>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Fiscal Period</p>
        {periodsLoading && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Loading periods…</p>}
        {periodsError  && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{periodsError}</p>}
        {!periodsLoading && !periodsError && (
          <select value={selectedPeriodId}
            onChange={(e) => { setSelectedPeriodId(e.target.value); setResult(null); setError(null); setSpecificDate('') }}
            className={`${inputClass} w-64`}>
            <option value="">— Select period —</option>
            {periods.map(p => (
              <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                {p.period_label ?? p.period_code}
                {p.period_status === 'open' ? ' (open)' : p.period_status === 'locked' ? ' (locked)' : ''}
              </option>
            ))}
          </select>
        )}
        {selectedPeriod && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{periodStart} – {periodEnd}</p>}
      </div>

      {selectedPeriod && (
        <div className="flex flex-col gap-2">
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Rate Date Selection</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {DATE_STRATEGIES.map((s) => (
              <button key={s.value} type="button" onClick={() => setStrategy(s.value)}
                className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  strategy === s.value
                    ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/10'
                    : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]'
                }`}>
                <p className={`text-tok-xs font-semibold ${strategy === s.value ? 'text-[rgb(var(--color-primary))]' : 'text-[rgb(var(--color-text))]'}`}>{s.label}</p>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{s.description}</p>
              </button>
            ))}
          </div>
          {strategy === 'specific' && (
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mt-1">
              Specific date (must be within {periodStart} – {periodEnd})
              <input type="date" min={periodStart} max={periodEnd} value={specificDate}
                onChange={(e) => setSpecificDate(e.target.value)} className={`${inputClass} w-48`} />
            </label>
          )}
        </div>
      )}

      <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/20 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
        ⚠ Your period, rate date, and source selection are recorded in the rate set and visible to the approver.
        All pulled rates will be <strong>Pending Approval</strong> — approve the rate set header to make them engine-eligible.
      </div>

      {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}

      {!result && (
        <div className="flex gap-2">
          <button type="button" onClick={handlePull} disabled={pulling || !selectedPeriodId || periodsLoading}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
            {pulling ? 'Pulling from ECB…' : 'Pull from ECB'}
          </button>
          <button type="button" onClick={onCancel} disabled={pulling}
            className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Cancel</button>
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-3">
          <div className={`rounded-lg px-3 py-2 text-tok-sm font-medium ${result.outcome === 'ok' ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'}`}>
            {result.message}
            {result.date_note && <span className="ml-2 text-tok-xs font-normal opacity-80">({result.date_note})</span>}
          </div>

          {summary && (
            <div className="flex gap-3 flex-wrap">
              {[['inserted','success'],['updated','info'],['skipped','muted'],['failed','danger']].map(([k, color]) => (
                summary[k] > 0 && (
                  <span key={k} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-tok-xs font-medium bg-[rgb(var(--color-${color}))]/10 text-[rgb(var(--color-${color}))]`}>
                    {k}: {summary[k]}
                  </span>
                )
              ))}
            </div>
          )}

          {/* S956: rate_set_id shown — directs CA to approve */}
          {result.rate_set_id && (
            <div className="rounded-lg bg-[rgb(var(--color-primary))]/10 border border-[rgb(var(--color-primary))]/20 px-3 py-2 text-tok-xs text-[rgb(var(--color-primary))]">
              Rate set created. Proceed to approve the rate set to make all rates engine-eligible.
            </div>
          )}

          {hasResults && (
            <div className="overflow-auto max-h-64 rounded-xl border border-[rgb(var(--color-border))]">
              <table className="w-full text-tok-xs">
                <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                  <tr>
                    {/* audit bug 6 S955: header shows local→base orientation */}
                    {['Currency', `Rate (local→${baseCurrencyCode ?? 'base'})`, 'Effective Date', 'Outcome', 'Note'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r) => (
                    <tr key={r.currency_code} className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]">
                      {/* audit bug 6 S955: local→base orientation */}
                      <td className="px-3 py-1.5 font-mono font-medium text-[rgb(var(--color-text))]">{r.currency_code} → {baseCurrencyCode ?? '?'}</td>
                      <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{r.rate ? Number(r.rate).toFixed(6) : '—'}</td>
                      <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{r.effective_date || '—'}</td>
                      <td className="px-3 py-1.5"><OutcomeBadge outcome={r.outcome} /></td>
                      <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{r.note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button type="button" onClick={onCancel}
            className="self-start rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
            Done
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// FXRateManager — main component
// ---------------------------------------------------------------------------
const PAGE_SIZE_OPTIONS = [25, 50, 100]

export default function FXRateManager({ fiscalPeriodId, fiscalPeriodLabel, periodStart, periodEnd, wizardSessionId }) {
  const { user } = useAuth()
  const isCompAdmin = user?.user_type === 'comp_admin'

  const [rates, setRates]               = useState([])
  const [count, setCount]               = useState(0)
  const [filters, setFilters]           = useState(INITIAL_FILTERS)
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState(null)

  // panel: null | 'create' | 'ecb' | 'rate_set_approve'
  const [panel, setPanel]               = useState(null)
  const [pendingRateSetId, setPendingRateSetId] = useState(null)

  const [approvingId, setApprovingId]   = useState(null)
  const [approvingAll, setApprovingAll] = useState(false)
  const [approveError, setApproveError] = useState(null)
  const [successMsg, setSuccessMsg]     = useState(null)

  // Table enhancements
  const [tableCollapsed, setTableCollapsed] = useState(false)
  const [search, setSearch]                 = useState('')
  const [selectedIds, setSelectedIds]       = useState(new Set())
  const [page, setPage]                     = useState(1)
  const [pageSize, setPageSize]             = useState(PAGE_SIZE_OPTIONS[0])

  // Amendment state — row-level approve only (manual rows, rate_set_id = NULL)
  const [amendModalOpen, setAmendModalOpen]     = useState(false)
  const [amendmentId, setAmendmentId]           = useState(null)
  const [amendEntityId, setAmendEntityId]       = useState(null)
  const [pendingApproveId, setPendingApproveId] = useState(null)
  const [pendingApproveFpId, setPendingApproveFpId] = useState(null)

  // C3 S955 — Base currency display + ECBPullPanel orientation
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null)
  useEffect(() => {
    fetch(`${API_BASE}/tenant-context`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.reporting_currency_code) setBaseCurrencyCode(d.reporting_currency_code) })
      .catch(() => {})
  }, [])

  // Ref for PendingRateSetsPanel refresh callback
  const refreshPendingSetsRef = React.useRef(null)

  // Sort
  const [sortCol, setSortCol] = useState('effective_date')
  const [sortDir, setSortDir] = useState('desc')
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
    setPage(1)
  }
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filteredRates = useMemo(() => {
    if (!search.trim()) return rates
    const q = search.trim().toUpperCase()
    return rates.filter(r =>
      r.from_currency_code?.trim().toUpperCase().includes(q) ||
      r.to_currency_code?.trim().toUpperCase().includes(q)
    )
  }, [rates, search])

  const sortedRates = useMemo(() => {
    return [...filteredRates].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'pair')                { av = `${a.from_currency_code}${a.to_currency_code}`; bv = `${b.from_currency_code}${b.to_currency_code}` }
      else if (sortCol === 'rate')           { av = Number(a.rate ?? 0); bv = Number(b.rate ?? 0) }
      else if (sortCol === 'effective_date') { av = a.effective_date ?? ''; bv = b.effective_date ?? '' }
      else if (sortCol === 'period_type')    { av = a.period_type ?? ''; bv = b.period_type ?? '' }
      else if (sortCol === 'rate_source')    { av = a.rate_source ?? ''; bv = b.rate_source ?? '' }
      else if (sortCol === 'status')         { av = a.is_locked ? '2' : a.rate_approved_by ? '1' : '0'; bv = b.is_locked ? '2' : b.rate_approved_by ? '1' : '0' }
      else if (sortCol === 'approved_at')    { av = a.rate_approved_at ?? ''; bv = b.rate_approved_at ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredRates, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(sortedRates.length / pageSize))
  const pagedRates = useMemo(() => sortedRates.slice((page - 1) * pageSize, page * pageSize), [sortedRates, page, pageSize])

  useEffect(() => { setPage(1) }, [search, filters])

  const pendingRates = useMemo(() => rates.filter(r => !r.rate_approved_by && !r.is_locked), [rates])
  // Manual-only pending rates (no rate_set_id) — eligible for row-level bulk approve
  const manualPendingRates = useMemo(() => pendingRates.filter(r => !r.rate_set_id), [pendingRates])

  const selectedPendingRates = useMemo(() =>
    manualPendingRates.filter(r => selectedIds.has(r.exchange_rate_id)),
  [manualPendingRates, selectedIds])

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleSelectAllPage = () => {
    const pageApprovable = pagedRates.filter(r => !r.rate_approved_by && !r.is_locked && !r.rate_set_id)
    const allSelected = pageApprovable.every(r => selectedIds.has(r.exchange_rate_id))
    setSelectedIds(prev => {
      const next = new Set(prev)
      pageApprovable.forEach(r => allSelected ? next.delete(r.exchange_rate_id) : next.add(r.exchange_rate_id))
      return next
    })
  }
  const selectAllPending = () => setSelectedIds(new Set(manualPendingRates.map(r => r.exchange_rate_id)))
  const clearSelection   = () => setSelectedIds(new Set())

  const loadRates = useCallback(async (activeFilters = filters) => {
    setLoading(true); setError(null)
    const params = new URLSearchParams()
    if (activeFilters.from_currency) params.set('from_currency', activeFilters.from_currency.toUpperCase())
    if (activeFilters.to_currency)   params.set('to_currency',   activeFilters.to_currency.toUpperCase())
    if (activeFilters.is_locked !== '') params.set('is_locked', activeFilters.is_locked)
    if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId))
    params.set('limit', '200')
    try {
      const res = await fetch(`${API_BASE}/exchange-rates?${params.toString()}`, { credentials: 'include' })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      setRates(getRows(payload))
      setCount(payload?.count ?? getRows(payload).length)
      setSelectedIds(new Set())
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setLoading(false)
    }
  }, [filters, fiscalPeriodId])

  useEffect(() => { loadRates() }, [loadRates])

  // ---------------------------------------------------------------------------
  // ECB pull complete — S956: capture rate_set_id, open approval panel
  // ---------------------------------------------------------------------------
  const handlePullComplete = (rateSetId) => {
    loadRates()
    if (refreshPendingSetsRef.current) refreshPendingSetsRef.current()
    if (rateSetId && isCompAdmin) {
      setPendingRateSetId(rateSetId)
      setPanel('rate_set_approve')
    } else {
      setPanel(null)
    }
  }

  // ---------------------------------------------------------------------------
  // Rate-set approval handlers — S956
  // ---------------------------------------------------------------------------
  const handleApproveSingleSet = (rateSetId) => {
    setPendingRateSetId(rateSetId)
    setPanel('rate_set_approve')
    setSuccessMsg(null)
    setApproveError(null)
  }

  const handleRateSetApproved = () => {
    loadRates()
    if (refreshPendingSetsRef.current) refreshPendingSetsRef.current()
    setSuccessMsg('Rate set approved. All rates are now engine-eligible.')
  }

  // ---------------------------------------------------------------------------
  // Row-level approve — manual rows only (rate_set_id = NULL)
  // ---------------------------------------------------------------------------
  const handleApproveClick = (exchangeRateId, rateFiscalPeriodId) => {
    if (!isCompAdmin) return
    if (amendmentId && amendEntityId === String(exchangeRateId)) {
      runApprove(exchangeRateId, amendmentId, rateFiscalPeriodId); return
    }
    setPendingApproveId(exchangeRateId)
    setPendingApproveFpId(rateFiscalPeriodId ?? null)
    setAmendEntityId(String(exchangeRateId))
    setAmendModalOpen(true)
  }

  const runApprove = async (exchangeRateId, amdId, rateFiscalPeriodId) => {
    setApprovingId(exchangeRateId); setApproveError(null); setSuccessMsg(null)
    try {
      const body = {}
      if (amdId) body.amendment_id = amdId
      const fpId = rateFiscalPeriodId ?? fiscalPeriodId
      if (fpId) body.fiscal_period_id = Number(fpId)
      const res = await fetch(`${API_BASE}/exchange-rates/${exchangeRateId}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setApproveError(getErrorMessage(payload)); return }
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null); setAmendEntityId(null) }
      setSuccessMsg('Rate approved.')
      const approved = payload?.exchange_rate || payload?.data || payload
      setRates((prev) => prev.map((r) => r.exchange_rate_id === exchangeRateId ? { ...r, ...approved } : r))
      setSelectedIds(prev => { const next = new Set(prev); next.delete(exchangeRateId); return next })
    } catch (e) {
      setApproveError(e.message || 'Network error.')
    } finally {
      setApprovingId(null)
    }
  }

  const handleAmendmentBegin = (newAmendmentId) => {
    setAmendmentId(newAmendmentId); setAmendModalOpen(false)
    if (pendingApproveId) { runApprove(pendingApproveId, newAmendmentId, pendingApproveFpId); setPendingApproveId(null); setPendingApproveFpId(null) }
  }
  const handleAmendModalCancel = () => { setAmendModalOpen(false); setPendingApproveId(null); setAmendEntityId(null) }

  // ---------------------------------------------------------------------------
  // Bulk approve — manual rows only (rate_set_id = NULL)
  // Governed batch rows use rate-set approve path
  // ---------------------------------------------------------------------------
  const runBulkApprove = async (targetRates, fetchAllPending = false) => {
    if (!isCompAdmin) return
    setApprovingAll(true); setApproveError(null); setSuccessMsg(null)
    let allRates = targetRates
    if (fetchAllPending) {
      try {
        const params = new URLSearchParams()
        params.set('is_locked', 'false')
        if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId))
        params.set('limit', '1000')
        const res = await fetch(`${API_BASE}/exchange-rates?${params.toString()}`, { credentials: 'include' })
        const payload = await res.json().catch(() => ({}))
        const all = getRows(payload)
        // Only manual rows (no rate_set_id) — governed rows use rate-set approve
        allRates = all.filter(r => !r.rate_approved_by && !r.is_locked && !r.rate_set_id)
      } catch (e) {
        setApproveError('Failed to fetch pending rates: ' + (e.message || 'Network error.'))
        setApprovingAll(false); return
      }
    }
    if (allRates.length === 0) { setApprovingAll(false); setSuccessMsg('No manual pending rates to approve.'); return }
    let approved = 0; let failed = 0
    for (const rate of allRates) {
      try {
        const body = {}
        const fpId = rate.fiscal_period_id ?? fiscalPeriodId
        if (fpId) body.fiscal_period_id = Number(fpId)
        const res = await fetch(`${API_BASE}/exchange-rates/${rate.exchange_rate_id}/approve`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        if (res.ok) {
          const payload = await res.json().catch(() => ({}))
          const updated = payload?.exchange_rate || payload?.data || payload
          setRates((prev) => prev.map((r) => r.exchange_rate_id === rate.exchange_rate_id ? { ...r, ...updated } : r))
          setSelectedIds(prev => { const next = new Set(prev); next.delete(rate.exchange_rate_id); return next })
          approved++
        } else { failed++ }
      } catch (_) { failed++ }
    }
    setApprovingAll(false)
    setSuccessMsg(`Bulk approve complete: ${approved} approved${failed > 0 ? `, ${failed} failed` : ''}.`)
  }

  const handleCreated = (created) => {
    setPanel(null)
    setSuccessMsg('Exchange rate created. Pending approval before engine use.')
    if (created?.exchange_rate_id) { setRates((prev) => [created, ...prev]); setCount((c) => c + 1) } else { loadRates() }
  }

  const handleApplyFilters = () => { loadRates(filters); setPage(1) }
  const handleClearFilters = () => { setFilters(INITIAL_FILTERS); loadRates(INITIAL_FILTERS); setPage(1) }
  const setFilter = (field, value) => setFilters((f) => ({ ...f, [field]: value }))

  const inputClass = 'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'

  const pageApprovable   = pagedRates.filter(r => !r.rate_approved_by && !r.is_locked && !r.rate_set_id)
  const allPageSelected  = pageApprovable.length > 0 && pageApprovable.every(r => selectedIds.has(r.exchange_rate_id))
  const somePageSelected = pageApprovable.some(r => selectedIds.has(r.exchange_rate_id))
  const colSpan          = isCompAdmin ? 10 : 7  // +1 for rate_set column

  return (
    <div className="flex flex-col gap-4">

      <AmendmentBeginModal
        open={amendModalOpen}
        entityType={GOVERNED_ENTITY_TYPE}
        entityId={amendEntityId}
        fiscalPeriodId={fiscalPeriodId ? Number(fiscalPeriodId) : null}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={handleAmendModalCancel}
      />

      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">FX</p>
            <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">FX Rate Manager</h2>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
              {fiscalPeriodLabel ? `Exchange rates for ${fiscalPeriodLabel}. ` : 'Exchange rates across all periods. '}
              Pull or import rates, then approve the rate set to make all rates engine-eligible in one governed event.
            </p>
          </div>
          {/* C3 S955 — Base currency pill */}
          {baseCurrencyCode && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 shrink-0">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Base currency</p>
              <p className="text-tok-sm font-semibold font-mono text-[rgb(var(--color-text))]">{baseCurrencyCode}</p>
            </div>
          )}
        </div>
      </div>

      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))] flex items-center justify-between">
          <span>⚠ Amendment open — rate approval is governed. Approve to commit.</span>
          <button type="button" onClick={async () => {
            try { await fetch(`${API_BASE}/amendments/${amendmentId}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }) } catch (_) {}
            setAmendmentId(null); setAmendEntityId(null)
          }} className="ml-3 text-tok-xs underline hover:no-underline">Cancel amendment</button>
        </div>
      )}

      {successMsg && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))] flex items-center justify-between">
          {successMsg}
          <button type="button" onClick={() => setSuccessMsg(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}
      {approveError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))] flex items-center justify-between">
          {approveError}
          <button type="button" onClick={() => setApproveError(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* S956: Pending rate sets panel — shown above rate table */}
      {isCompAdmin && panel === null && (
        <PendingRateSetsPanel
          fiscalPeriodId={fiscalPeriodId}
          baseCurrencyCode={baseCurrencyCode}
          onSetApproved={refreshPendingSetsRef}
          onApproveSingleSet={handleApproveSingleSet}
        />
      )}

      {/* Action panels */}
      {panel === 'create' && isCompAdmin && (
        <CreateForm onCreated={handleCreated} onCancel={() => setPanel(null)} />
      )}
      {panel === 'ecb' && isCompAdmin && (
        <ECBPullPanel
          defaultFiscalPeriodId={fiscalPeriodId}
          baseCurrencyCode={baseCurrencyCode}
          onPullComplete={handlePullComplete}
          onCancel={() => setPanel(null)}
        />
      )}
      {/* S956: Rate set approval panel */}
      {panel === 'rate_set_approve' && isCompAdmin && pendingRateSetId && (
        <RateSetApprovalPanel
          rateSetId={pendingRateSetId}
          baseCurrencyCode={baseCurrencyCode}
          onApproved={handleRateSetApproved}
          onCancel={() => { setPanel(null); setPendingRateSetId(null) }}
        />
      )}

      {/* Rate table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setTableCollapsed(c => !c)}
              className="rounded p-0.5 hover:bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
              title={tableCollapsed ? 'Expand table' : 'Collapse table'}>
              {tableCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Exchange Rates
              {!loading && (
                <span className="font-normal text-[rgb(var(--color-text-muted))]">
                  {' '}({filteredRates.length}{filteredRates.length !== count ? ` of ${count}` : ''})
                </span>
              )}
            </h3>
          </div>

          {isCompAdmin && panel === null && (
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => { setPanel('ecb'); setSuccessMsg(null) }}
                className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))]">
                ↓ Pull from ECB
              </button>
              <button type="button" onClick={() => { setPanel('create'); setSuccessMsg(null) }}
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                + Manual Rate
              </button>
            </div>
          )}
        </div>

        {!tableCollapsed && (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Search pair
                <input type="text" placeholder="USD, EUR…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputClass} w-36`} />
              </label>
              <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">From
                <input type="text" maxLength={3} placeholder="EUR" value={filters.from_currency} onChange={(e) => setFilter('from_currency', e.target.value)} className={`${inputClass} w-20`} />
              </label>
              <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">To
                <input type="text" maxLength={3} placeholder="USD" value={filters.to_currency} onChange={(e) => setFilter('to_currency', e.target.value)} className={`${inputClass} w-20`} />
              </label>
              <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status
                <select value={filters.is_locked} onChange={(e) => setFilter('is_locked', e.target.value)} className={`${inputClass} w-36`}>
                  <option value="">All</option>
                  <option value="false">Pending / Approved</option>
                  <option value="true">Locked</option>
                </select>
              </label>
              <div className="flex gap-2 pb-0.5">
                <button type="button" onClick={handleApplyFilters} disabled={loading} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">Apply</button>
                <button type="button" onClick={handleClearFilters} disabled={loading} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Clear</button>
              </div>
            </div>

            {/* Multi-select action bar — manual rows only */}
            {isCompAdmin && (
              <div className="flex flex-wrap items-center gap-2 min-h-[2rem]">
                {selectedIds.size > 0 ? (
                  <>
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{selectedIds.size} selected (manual rates)</span>
                    <button type="button" onClick={() => runBulkApprove(selectedPendingRates)} disabled={approvingAll || selectedPendingRates.length === 0}
                      className="rounded-lg bg-[rgb(var(--color-success))] px-3 py-1 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50">
                      {approvingAll ? 'Approving…' : `Approve Selected (${selectedPendingRates.length})`}
                    </button>
                    <button type="button" onClick={clearSelection}
                      className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                      Clear selection
                    </button>
                  </>
                ) : manualPendingRates.length > 0 ? (
                  <>
                    <button type="button" onClick={selectAllPending}
                      className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                      Select all manual pending ({manualPendingRates.length})
                    </button>
                    <button type="button" onClick={() => runBulkApprove(manualPendingRates, true)} disabled={approvingAll}
                      className="rounded-lg bg-[rgb(var(--color-success))] px-3 py-1 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50">
                      {approvingAll ? 'Approving…' : `Approve All Manual Pending (${manualPendingRates.length})`}
                    </button>
                  </>
                ) : null}
              </div>
            )}

            {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}

            <div className="overflow-auto max-h-[600px] rounded-xl border border-[rgb(var(--color-border))]">
              <table className="w-full text-tok-sm">
                <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                  <tr>
                    {isCompAdmin && (
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox" checked={allPageSelected}
                          ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected }}
                          onChange={toggleSelectAllPage} className="rounded"
                          title="Select all manual approvable on this page" />
                      </th>
                    )}
                    {[['pair','Pair'],['rate','Rate'],['effective_date','Effective Date'],['period_type','Type'],['rate_source','Source'],['status','Status'],['approved_at','Approved At']].map(([col, label]) => (
                      <th key={col} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]" onClick={() => toggleSort(col)}>
                        {label}<SortIcon col={col} />
                      </th>
                    ))}
                    {/* S956: rate set column */}
                    <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Rate Set</th>
                    {isCompAdmin && <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={colSpan} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading exchange rates…</td></tr>}
                  {!loading && pagedRates.length === 0 && <tr><td colSpan={colSpan} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">No exchange rates found.</td></tr>}
                  {!loading && pagedRates.map((rate) => {
                    const isManualPending = !rate.rate_approved_by && !rate.is_locked && !rate.rate_set_id
                    const isSetPending    = !rate.rate_approved_by && !rate.is_locked && !!rate.rate_set_id
                    const canApproveRow   = isCompAdmin && isManualPending
                    const isApproving     = approvingId === rate.exchange_rate_id
                    const isSelected      = selectedIds.has(rate.exchange_rate_id)
                    return (
                      <tr key={rate.exchange_rate_id}
                        className={`border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))] ${isSelected ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}>
                        {isCompAdmin && (
                          <td className="px-3 py-2.5">
                            {canApproveRow && (
                              <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(rate.exchange_rate_id)} className="rounded" />
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-tok-sm font-medium text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(rate.from_currency_code).trim()} → {fmt(rate.to_currency_code).trim()}</td>
                        <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] font-mono whitespace-nowrap">{fmtRate(rate.rate)}</td>
                        <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{fmtDate(rate.effective_date)}</td>
                        <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(rate.period_type)}</td>
                        <td className="px-3 py-2.5 text-[rgb(var(--color-text))]"><SourceBadge source={rate.rate_source} /></td>
                        <td className="px-3 py-2.5 text-[rgb(var(--color-text))]"><StatusBadge rate={rate} /></td>
                        <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text-muted))] whitespace-nowrap">{fmtDateTime(rate.rate_approved_at)}</td>
                        {/* S956: rate set indicator */}
                        <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {rate.rate_set_id
                            ? <button type="button" onClick={() => isCompAdmin && handleApproveSingleSet(rate.rate_set_id)}
                                className={`font-mono truncate max-w-[80px] block ${isCompAdmin && isSetPending ? 'text-[rgb(var(--color-warning))] hover:underline cursor-pointer' : ''}`}
                                title={rate.rate_set_id}>
                                {String(rate.rate_set_id).slice(0, 8)}…
                              </button>
                            : <span className="text-[rgb(var(--color-text-muted))]">manual</span>
                          }
                        </td>
                        {isCompAdmin && (
                          <td className="px-3 py-2.5 text-[rgb(var(--color-text))]">
                            {canApproveRow ? (
                              <button type="button" onClick={() => handleApproveClick(rate.exchange_rate_id, rate.fiscal_period_id)} disabled={isApproving || approvingAll}
                                className="rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50">
                                {isApproving ? 'Approving…' : 'Approve'}
                              </button>
                            ) : isSetPending ? (
                              <button type="button" onClick={() => handleApproveSingleSet(rate.rate_set_id)}
                                className="rounded-lg border border-[rgb(var(--color-warning))]/50 px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-warning))] hover:bg-[rgb(var(--color-warning))]/10">
                                Approve Set
                              </button>
                            ) : (
                              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rate.is_locked ? 'Locked' : 'Approved'}</span>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {sortedRates.length} rates{totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                </span>
                <label className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Rows per page
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                    className="rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-1.5 py-0.5 text-tok-xs">
                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
              {totalPages > 1 && (
                <div className="flex gap-1">
                  <button type="button" onClick={() => setPage(1)} disabled={page === 1}
                    className="rounded px-2 py-1 text-tok-xs border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">«</button>
                  <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="rounded px-2 py-1 text-tok-xs border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = Math.min(Math.max(page - 2, 1) + i, totalPages - Math.min(5, totalPages) + i + 1)
                    return (
                      <button key={p} type="button" onClick={() => setPage(p)}
                        className={`rounded px-2 py-1 text-tok-xs border ${p === page ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]' : 'border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]'}`}>
                        {p}
                      </button>
                    )
                  })}
                  <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="rounded px-2 py-1 text-tok-xs border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">›</button>
                  <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages}
                    className="rounded px-2 py-1 text-tok-xs border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">»</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
